/**
 * The settings search index.
 *
 * Settings is a dozen screens, and the owner's complaint that started
 * this work was that he could not remember which one holds a given switch.
 * The nav answers "where do I browse"; this answers "where is THAT setting".
 *
 * PURE ON PURPOSE: no React, no i18n instance, no DOM. The matcher takes the
 * translator as an argument, so the whole thing is unit-testable and so the
 * index cannot quietly grow a dependency on the component that renders it.
 *
 * The index is hand-maintained rather than scraped from the pages, because a
 * scrape can only find the labels a page happens to render today — it cannot
 * know that "rounding" is what the owner calls `checkoutRoundingStepIqd`, and
 * the keywords are half the value here. The cost is that a NEW SETTING MUST BE
 * ADDED HERE TOO; `settingsSearch.test.ts` checks every entry still points at
 * a real page, a real anchor and a real translation key, so a rename breaks
 * the suite rather than the search box.
 */

/** A single searchable setting. */
export interface SettingsSearchEntry {
  /** Stable id — used as the React key and in tests, never shown. */
  id: string;
  /** i18n key of the setting's own label, as its page renders it. */
  labelKey: string;
  /** i18n key of the page it lives on; shown under the label as context. */
  pageKey: string;
  /** The settings route that holds it. */
  path: string;
  /**
   * The `<Section id>` to scroll to on that page. Omitted for a screen that is
   * one list end to end — jumping to its top is the whole answer.
   */
  anchor?: string;
  /**
   * Untranslated aliases: what the owner or a cashier might type instead of
   * the label. Matching also runs over the TRANSLATED label, so these do not
   * need Arabic or Kurdish copies of the label itself — they carry the English
   * synonyms, the hardware words and the old names a screen used to have.
   */
  keywords: readonly string[];
  /**
   * Permission this setting's section needs before it renders at all.
   *
   * `KioskUpdateSection` returns null without `devices.manage`, so for a
   * cashier the "Kiosk app" result used to navigate to an anchor that never
   * appears — the page simply sat at the top with nothing to show for the
   * press. A result that goes nowhere is worse than no result, so entries that
   * depend on a permission declare it and are filtered out for everyone else.
   */
  requires?: string;
}

const VENUE = 'settings.general.title';
const PRINTING = 'settings.general.printingTitle';
const PORTAL = 'settings.general.portalTitle';
const LOYALTY = 'settings.general.loyaltyTitle';

export const SETTINGS_SEARCH_INDEX: readonly SettingsSearchEntry[] = [
  // ---- Venue (/settings/general) ----
  {
    // Lived on the Public page until that page was removed (2026-09-24).
    id: 'venue-name',
    labelKey: 'settings.general.venueName',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-identity',
    keywords: ['name', 'venue', 'rename', 'receipt', 'title', 'public'],
  },
  {
    id: 'staff-colours',
    labelKey: 'settings.general.staffColoursSection',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-staff-colours',
    keywords: ['colour', 'color', 'brand', 'theme', 'primary', 'secondary', 'accent', 'reset'],
  },
  {
    id: 'logo',
    labelKey: 'settings.general.logo',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-identity',
    keywords: ['logo', 'brand', 'image', 'picture'],
  },
  {
    id: 'wifi-qr',
    labelKey: 'settings.general.wifiQr',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-identity',
    keywords: ['wifi', 'wi-fi', 'qr', 'internet', 'password', 'guest'],
  },
  {
    id: 'slogan',
    labelKey: 'settings.general.slogan',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-identity',
    keywords: ['slogan', 'tagline', 'motto'],
  },
  {
    id: 'opening-time',
    labelKey: 'settings.general.openingTime',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-business-day',
    keywords: ['opening', 'open', 'business day', 'shift', 'hours'],
  },
  {
    id: 'closing-time',
    labelKey: 'settings.general.closingTime',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-business-day',
    keywords: ['closing', 'close', 'business day', 'end of day', 'hours'],
  },
  {
    id: 'currency-symbol',
    labelKey: 'settings.general.currencySymbol',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['currency', 'symbol', 'iqd', 'dinar', 'money'],
  },
  {
    id: 'monthly-target',
    labelKey: 'settings.general.monthlyTarget',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['target', 'monthly', 'goal', 'revenue'],
  },
  {
    id: 'rounding-rule',
    labelKey: 'settings.general.roundTheAmount',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['rounding', 'round', 'checkout', 'bill', 'amount'],
  },
  {
    id: 'rounding-step',
    labelKey: 'settings.general.roundingStep',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['rounding', 'round', 'step', '250', 'nearest'],
  },
  {
    id: 'service-charge',
    labelKey: 'venueServiceCharge.percent',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['service', 'service charge', 'tip', 'surcharge', 'percent', 'receipt', 'waiter'],
  },
  {
    id: 'service-charge-scope',
    labelKey: 'venueServiceCharge.scope',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['service', 'service charge', 'items', 'reservations', 'cafe', 'play time'],
  },
  {
    id: 'negative-sales',
    labelKey: 'settings.general.enableNegativeSales',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['negative', 'sales', 'oversell', 'stock'],
  },
  {
    id: 'balance-refund',
    labelKey: 'settings.general.enableBalanceRefund',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-money',
    keywords: ['refund', 'balance', 'wallet', 'cash out'],
  },
  {
    // Worked long before it had a control (2026-09-24).
    id: 'about-to-end',
    labelKey: 'settings.general.aboutToEnd',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-warnings',
    keywords: ['about to end', 'ending', 'warning', 'amber', 'minutes', 'time', 'session'],
  },
  {
    id: 'low-stock-default',
    labelKey: 'settings.general.lowStockDefault',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-warnings',
    keywords: ['low stock', 'stock', 'inventory', 'threshold', 'alert', 'dashboard', 'reorder'],
  },
  {
    id: 'boot-camp',
    labelKey: 'settings.general.enableBootCampOffer',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-features',
    keywords: ['boot camp', 'day pass', 'offer'],
  },
  {
    id: 'reservation-alerts',
    labelKey: 'settings.general.enableReservationAlerts',
    pageKey: VENUE,
    path: '/settings/general',
    anchor: 'set-features',
    keywords: ['reservation', 'booking', 'alert', 'notification'],
  },

  // ---- Printing (/settings/printing) ----
  {
    id: 'printers',
    // `printersSection`, the heading the page actually renders — not the
    // identically-worded `settings.general.printers`, which the old single
    // page used and nothing renders any more. Same word in all three locales
    // today, so pointing at the live key is free; leaving it on the dead one
    // means a retranslation of the heading silently stops matching here.
    labelKey: 'settings.general.printersSection',
    pageKey: PRINTING,
    path: '/settings/printing',
    anchor: 'set-printers',
    keywords: ['printer', 'printers', 'thermal', 'usb', 'network', 'kitchen', 'bar'],
  },
  {
    id: 'default-printer',
    labelKey: 'settings.general.defaultPrinter',
    pageKey: PRINTING,
    path: '/settings/printing',
    anchor: 'set-printers',
    keywords: ['default printer', 'receipt printer'],
  },
  {
    id: 'copy-printing',
    labelKey: 'settings.general.copyPrinting',
    pageKey: PRINTING,
    path: '/settings/printing',
    anchor: 'set-receipt',
    keywords: ['copies', 'copy', 'receipt', 'duplicate'],
  },
  {
    id: 'printing-phone',
    labelKey: 'settings.general.phoneForPrinting',
    pageKey: PRINTING,
    path: '/settings/printing',
    anchor: 'set-receipt',
    keywords: ['phone', 'receipt', 'contact'],
  },
  {
    id: 'printing-address',
    labelKey: 'settings.general.printingAddress',
    pageKey: PRINTING,
    path: '/settings/printing',
    anchor: 'set-receipt',
    keywords: ['address', 'receipt', 'footer'],
  },
  {
    id: 'print-logo',
    labelKey: 'settings.general.printLogo.title',
    pageKey: PRINTING,
    path: '/settings/printing',
    anchor: 'set-receipt',
    keywords: ['logo', 'receipt', 'print', 'header'],
  },

  // ---- Portal & kiosk (/settings/portal) ----
  {
    // Owner, 2026-09-28. The keywords carry every section's English name and
    // the words the owner used for them ("Challenges" is the Arcade tab), so
    // typing the thing he wants gone finds the switch that hides it.
    id: 'portal-sections',
    labelKey: 'settings.general.portalSections.title',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-portal-sections',
    keywords: [
      'hide',
      'show',
      'section',
      'tab',
      'portal',
      'home',
      'my session',
      'games',
      'apps',
      'arcade',
      'challenges',
      'quests',
      'prize vault',
      'shop',
      'summon',
      'call staff',
      'help button',
    ],
  },
  {
    id: 'portal-account-creation',
    labelKey: 'settings.general.enablePortalAccountCreation',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-portal-accounts',
    keywords: ['portal', 'account', 'sign up', 'register', 'customer'],
  },
  {
    id: 'order-from-portal',
    labelKey: 'settings.general.enableOrderFromPortal',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-portal-orders',
    keywords: ['order', 'portal', 'cafe', 'menu', 'customer'],
  },
  {
    id: 'portal-payment-methods',
    labelKey: 'settings.general.portalPaymentMethods',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-portal-orders',
    keywords: ['payment', 'portal', 'wallet', 'cash', 'order'],
  },
  {
    id: 'hero-video',
    labelKey: 'settings.general.heroVideo',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-kiosk-screen',
    keywords: ['video', 'hero', 'idle', 'screen', 'kiosk', 'trailer'],
  },
  {
    id: 'login-with-card',
    labelKey: 'settings.general.enableLoginWithCard',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-kiosk-login',
    keywords: ['card', 'login', 'rfid', 'tap', 'kiosk'],
  },
  {
    id: 'login-hold-time',
    labelKey: 'settings.general.loginHoldTime',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-kiosk-login',
    keywords: ['hold', 'login', 'timeout', 'kiosk'],
  },
  {
    id: 'hotkey-combo',
    labelKey: 'settings.general.hotKey',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-hotkeys',
    keywords: ['hotkey', 'hot key', 'combo', 'shortcut', 'unlock', 'desktop', 'staff', 'keyboard'],
  },
  {
    id: 'sign-out-hotkey',
    labelKey: 'settings.general.signOutHotKey',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-hotkeys',
    keywords: ['hotkey', 'hot key', 'shortcut', 'sign out', 'logout', 'keyboard'],
  },
  {
    id: 'hotkey-password',
    labelKey: 'settings.general.hotKeyPassword',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-hotkeys',
    keywords: ['hotkey', 'password', 'unlock', 'kiosk', 'staff'],
  },
  {
    id: 'hotkey-enable-password',
    labelKey: 'settings.general.enablePassword',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-hotkeys',
    keywords: ['password', 'require', 'hotkey', 'unlock'],
  },
  {
    id: 'auto-relock',
    labelKey: 'settings.general.autoRelock',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-hotkeys',
    keywords: ['relock', 'lock', 'auto', 'minutes', 'kiosk', 'unattended'],
  },
  {
    id: 'central-device',
    labelKey: 'settings.general.centralDeviceIp',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-central-device',
    keywords: [
      'central',
      'central device',
      'ip',
      'lan',
      'network',
      'main pc',
      'bridge',
      'idle video',
      'download',
    ],
  },
  {
    id: 'kiosk-update',
    labelKey: 'settings.general.kioskSection',
    pageKey: PORTAL,
    path: '/settings/portal',
    anchor: 'set-kiosk-update',
    keywords: ['kiosk', 'update', 'version', 'agent', 'refresh', 'portal'],
    requires: 'devices.manage',
  },

  // ---- Loyalty & rewards (/settings/loyalty) ----
  {
    id: 'points-name',
    labelKey: 'settings.general.pointsName',
    pageKey: LOYALTY,
    path: '/settings/loyalty',
    anchor: 'set-points-name',
    keywords: ['points', 'name', 'rewards', 'coins'],
  },
  {
    id: 'points-per-hour',
    labelKey: 'settings.general.pointsPerHour',
    pageKey: LOYALTY,
    path: '/settings/loyalty',
    anchor: 'set-loyalty-rates',
    keywords: ['points', 'hour', 'rate', 'play', 'loyalty'],
  },
  {
    id: 'points-per-thousand',
    labelKey: 'settings.general.pointsPerThousand',
    pageKey: LOYALTY,
    path: '/settings/loyalty',
    anchor: 'set-loyalty-rates',
    keywords: ['points', 'spend', 'rate', 'loyalty', 'cashback'],
  },
  {
    id: 'refill-flat-percent',
    labelKey: 'settings.general.refillFlatPercent',
    pageKey: LOYALTY,
    path: '/settings/loyalty',
    anchor: 'set-loyalty-rates',
    keywords: ['refill', 'bonus', 'top up', 'percent', 'wallet'],
  },
  {
    id: 'refill-tiers',
    labelKey: 'settings.general.refillTiers',
    pageKey: LOYALTY,
    path: '/settings/loyalty',
    anchor: 'set-loyalty-rates',
    keywords: ['refill', 'tier', 'bonus', 'top up', 'wallet'],
  },

  // ---- The list screens. One entry for the screen, plus the settings inside
  //      it that nobody would guess live there. ----
  {
    id: 'employees',
    labelKey: 'settings.nav.employees',
    pageKey: 'settings.nav.employees',
    path: '/settings/employees',
    keywords: ['employee', 'staff', 'user', 'cashier', 'account', 'password'],
  },
  {
    id: 'employee-privileges',
    labelKey: 'settings.employees.privileges',
    pageKey: 'settings.nav.employees',
    path: '/settings/employees',
    keywords: ['permission', 'privilege', 'access', 'allow', 'employee'],
  },
  {
    id: 'roles',
    labelKey: 'settings.nav.roles',
    pageKey: 'settings.nav.roles',
    path: '/settings/roles',
    keywords: ['role', 'permission', 'template', 'group', 'access'],
  },
  {
    id: 'games',
    labelKey: 'settings.nav.games',
    pageKey: 'settings.nav.games',
    path: '/settings/games',
    keywords: ['game', 'title', 'cover', 'trailer', 'launch', 'steam', 'epic'],
  },
  {
    id: 'apps',
    labelKey: 'settings.nav.apps',
    pageKey: 'settings.nav.apps',
    path: '/settings/apps',
    keywords: ['app', 'application', 'icon', 'shortcut', 'launch'],
  },
  {
    id: 'game-accounts',
    labelKey: 'settings.nav.gameAccounts',
    pageKey: 'settings.nav.gameAccounts',
    path: '/settings/game-accounts',
    // "signed in" and "made for pc" since 2026-10-05: each account lives on
    // the PC staff keep it signed in on, and this is where that is set.
    keywords: [
      'account',
      'steam',
      'riot',
      'epic',
      'login',
      'password',
      'mailbox',
      'vault',
      'signed in',
      'made for pc',
    ],
  },
  {
    id: 'device-categories',
    labelKey: 'settings.nav.deviceCategories',
    pageKey: 'settings.nav.deviceCategories',
    path: '/settings/device-categories',
    keywords: ['device', 'category', 'pc', 'ps5', 'price', 'rate'],
  },
  {
    id: 'device-category-printer',
    labelKey: 'settings.deviceCategories.printer',
    pageKey: 'settings.nav.deviceCategories',
    path: '/settings/device-categories',
    keywords: ['printer', 'category', 'ticket', 'routing'],
  },
  {
    id: 'payments',
    labelKey: 'settings.nav.payments',
    pageKey: 'settings.nav.payments',
    path: '/settings/payments',
    keywords: ['payment', 'provider', 'fib', 'qi', 'fastpay', 'e-payment', 'electronics', 'api'],
  },
  {
    id: 'print-queue',
    labelKey: 'settings.nav.printQueue',
    pageKey: 'settings.nav.printQueue',
    path: '/settings/print-queue',
    keywords: ['print', 'queue', 'job', 'jobs', 'failed', 'retry', 'reprint', 'bridge'],
  },
];

/**
 * Folds a string down to what two people typing the same word have in common.
 *
 * Case, Latin accents and the Arabic-script variations that are the same
 * letter to a reader but different code points to `includes()`: the harakat a
 * locale file may carry and the typist never types, tatweel, the three alefs,
 * and the Kurdish ک/ی against the Arabic ك/ي — the venue's three languages
 * share an alphabet but not a keyboard, and a search that misses on those is
 * a search the owner stops using.
 */
export function normalizeSearchText(value: string): string {
  return (
    value
      .toLowerCase()
      // Splits é into e + combining accent, which the next step drops.
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      // Arabic harakat, the hamza/maddah marks NFD just split off the alefs,
      // superscript alef, and tatweel (a stretch, not a letter).
      .replace(/[ً-ٰٕـ]/g, '')
      .replace(/[آأإ]/g, 'ا')
      .replace(/[ىی]/g, 'ي')
      .replace(/ک/g, 'ك')
      .replace(/ھ|ہ/g, 'ه')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/** How well an entry answers the query; higher wins, 0 means "it does not". */
function score(entry: SettingsSearchEntry, needle: string, label: string, page: string): number {
  const normalizedLabel = normalizeSearchText(label);
  if (normalizedLabel === needle) return 100;
  if (normalizedLabel.startsWith(needle)) return 80;
  // A word inside the label, e.g. "card" in "Enable login with card".
  if (new RegExp(`(^| )${escapeRegExp(needle)}`).test(normalizedLabel)) return 70;
  if (normalizedLabel.includes(needle)) return 60;
  if (entry.keywords.some((keyword) => normalizeSearchText(keyword).startsWith(needle))) return 50;
  if (entry.keywords.some((keyword) => normalizeSearchText(keyword).includes(needle))) return 40;
  // The page's own name, so "printing" lists everything on the Printing page.
  if (normalizeSearchText(page).includes(needle)) return 20;
  return 0;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The entries matching `query`, best first.
 *
 * `translate` is passed in rather than imported so this module stays pure —
 * the caller hands it `i18n.t`, a test hands it a stub or a real bundle.
 * An empty query returns nothing: the box is a filter, not a second nav.
 *
 * `can` is optional and defaults to permitting everything, so a test or a
 * caller that does not care keeps the old one-line call. Where it IS passed,
 * an entry whose section needs a permission the reader lacks is dropped rather
 * than offered — see `requires`.
 */
export function searchSettings(
  query: string,
  translate: (key: string) => string,
  options: {
    index?: readonly SettingsSearchEntry[];
    limit?: number;
    can?: (permission: string) => boolean;
  } = {},
): readonly SettingsSearchEntry[] {
  const { index = SETTINGS_SEARCH_INDEX, limit = 8, can } = options;
  const needle = normalizeSearchText(query);
  if (!needle) return [];

  return (
    index
      .filter((entry) => entry.requires === undefined || can === undefined || can(entry.requires))
      .map((entry) => ({
        entry,
        rank: score(entry, needle, translate(entry.labelKey), translate(entry.pageKey)),
      }))
      .filter((row) => row.rank > 0)
      // Stable within a rank: `sort` is stable in every engine we ship on, so
      // equally-good matches stay in index order and the list does not reshuffle
      // as the owner types another letter.
      .sort((a, b) => b.rank - a.rank)
      .slice(0, limit)
      .map((row) => row.entry)
  );
}
